/**
 * Offline Context
 * Provides network status detection, offline data caching, and sync queue
 */

import { t } from "@lingui/macro";
import { CloudSlash as CloudSlashIcon, CloudCheck as CloudCheckIcon, ArrowsClockwise as ArrowsClockwiseIcon } from "@phosphor-icons/react";
import type { ResumeDto } from "@stylingcv/dto";
import { cn } from "@stylingcv/utils";
import { AnimatePresence, motion } from "framer-motion";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

// Types
type NetworkStatus = "online" | "offline" | "syncing";

type PendingChange = {
  id: string;
  timestamp: number;
  type: "resume_update" | "resume_create" | "resume_delete";
  data: Record<string, unknown>;
  retryCount: number;
};

type OfflineContextType = {
  isOnline: boolean;
  networkStatus: NetworkStatus;
  pendingChanges: PendingChange[];
  hasPendingChanges: boolean;
  // Actions
  cacheResumeData: (resume: ResumeDto) => void;
  getCachedResume: (id: string) => ResumeDto | null;
  getAllCachedResumes: () => ResumeDto[];
  addPendingChange: (change: Omit<PendingChange, "id" | "timestamp" | "retryCount">) => void;
  syncPendingChanges: () => Promise<void>;
  clearCache: () => void;
};

const OfflineContext = createContext<OfflineContextType | null>(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error("useOffline must be used within an OfflineProvider");
  }
  return context;
};

// Storage keys
const CACHE_KEY_PREFIX = "stylingcv_offline_resume_";
const PENDING_CHANGES_KEY = "stylingcv_pending_changes";

// Helper to generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

type OfflineProviderProps = {
  children: ReactNode;
  onSync?: (changes: PendingChange[]) => Promise<void>;
};

export const OfflineProvider = ({ children, onSync }: OfflineProviderProps) => {
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(isOnline ? "online" : "offline");
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);

  // Load pending changes from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(PENDING_CHANGES_KEY);
      if (stored) {
        setPendingChanges(JSON.parse(stored));
      }
    } catch (error) {
      console.error("Failed to load pending changes:", error);
    }
  }, []);

  // Save pending changes to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(PENDING_CHANGES_KEY, JSON.stringify(pendingChanges));
    } catch (error) {
      console.error("Failed to save pending changes:", error);
    }
  }, [pendingChanges]);

  // Cache resume data to localStorage
  const cacheResumeData = useCallback((resume: ResumeDto) => {
    try {
      const key = `${CACHE_KEY_PREFIX}${resume.id}`;
      localStorage.setItem(key, JSON.stringify({
        resume,
        cachedAt: Date.now(),
      }));
    } catch (error) {
      console.error("Failed to cache resume:", error);
    }
  }, []);

  // Get cached resume by ID
  const getCachedResume = useCallback((id: string): ResumeDto | null => {
    try {
      const key = `${CACHE_KEY_PREFIX}${id}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const { resume } = JSON.parse(stored);
        return resume;
      }
    } catch (error) {
      console.error("Failed to get cached resume:", error);
    }
    return null;
  }, []);

  // Get all cached resumes
  const getAllCachedResumes = useCallback((): ResumeDto[] => {
    const resumes: ResumeDto[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_KEY_PREFIX)) {
          const stored = localStorage.getItem(key);
          if (stored) {
            const { resume } = JSON.parse(stored);
            resumes.push(resume);
          }
        }
      }
    } catch (error) {
      console.error("Failed to get all cached resumes:", error);
    }
    return resumes;
  }, []);

  // Add a pending change to the queue
  const addPendingChange = useCallback((change: Omit<PendingChange, "id" | "timestamp" | "retryCount">) => {
    const newChange: PendingChange = {
      ...change,
      id: generateId(),
      timestamp: Date.now(),
      retryCount: 0,
    };
    setPendingChanges((prev) => [...prev, newChange]);
  }, []);

  // Sync pending changes when online
  const syncPendingChanges = useCallback(async () => {
    if (!isOnline || pendingChanges.length === 0) return;

    setNetworkStatus("syncing");

    try {
      if (onSync) {
        await onSync(pendingChanges);
        // Clear pending changes after successful sync
        setPendingChanges([]);
      } else {
        // Default sync behavior - just clear the queue since we don't have a handler
        // In a real implementation, you would call your API here
        console.log("Syncing pending changes:", pendingChanges);
        setPendingChanges([]);
      }
      setNetworkStatus("online");
    } catch (error) {
      console.error("Failed to sync pending changes:", error);
      // Mark changes for retry
      setPendingChanges((prev) =>
        prev.map((change) => ({
          ...change,
          retryCount: change.retryCount + 1,
        })),
      );
      setNetworkStatus("online");
    }
  }, [isOnline, pendingChanges, onSync]);

  // Listen for network status changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setNetworkStatus("online");
      // Trigger sync when coming back online
      if (pendingChanges.length > 0) {
        syncPendingChanges();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setNetworkStatus("offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [pendingChanges.length, syncPendingChanges]);

  // Clear all cached data
  const clearCache = useCallback(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      setPendingChanges([]);
      localStorage.removeItem(PENDING_CHANGES_KEY);
    } catch (error) {
      console.error("Failed to clear cache:", error);
    }
  }, []);

  const contextValue = useMemo<OfflineContextType>(
    () => ({
      isOnline,
      networkStatus,
      pendingChanges,
      hasPendingChanges: pendingChanges.length > 0,
      cacheResumeData,
      getCachedResume,
      getAllCachedResumes,
      addPendingChange,
      syncPendingChanges,
      clearCache,
    }),
    [isOnline, networkStatus, pendingChanges, cacheResumeData, getCachedResume, getAllCachedResumes, addPendingChange, syncPendingChanges, clearCache],
  );

  return (
    <OfflineContext.Provider value={contextValue}>
      {children}
    </OfflineContext.Provider>
  );
};

/**
 * OfflineIndicator - Shows current network status
 * Displays a subtle banner when offline or syncing
 */
type OfflineIndicatorProps = {
  className?: string;
  position?: "top" | "bottom";
};

export const OfflineIndicator = ({ className, position = "bottom" }: OfflineIndicatorProps) => {
  const { networkStatus, hasPendingChanges, syncPendingChanges } = useOffline();

  // Only show when offline or syncing
  if (networkStatus === "online" && !hasPendingChanges) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: position === "top" ? -20 : 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: position === "top" ? -20 : 20 }}
        className={cn(
          "fixed left-1/2 z-50 -translate-x-1/2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm",
          position === "top" ? "top-4" : "bottom-20",
          networkStatus === "offline" && "bg-red-600/90 text-white",
          networkStatus === "syncing" && "bg-blue-600/90 text-white",
          networkStatus === "online" && hasPendingChanges && "bg-amber-600/90 text-white",
          className,
        )}
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          {networkStatus === "offline" && (
            <>
              <CloudSlashIcon className="size-4" weight="bold" />
              <span>{t`You're offline`}</span>
            </>
          )}

          {networkStatus === "syncing" && (
            <>
              <ArrowsClockwiseIcon className="size-4 animate-spin" />
              <span>{t`Syncing...`}</span>
            </>
          )}

          {networkStatus === "online" && hasPendingChanges && (
            <>
              <ArrowsClockwiseIcon className="size-4" />
              <button
                onClick={() => syncPendingChanges()}
                className="underline underline-offset-2 hover:no-underline"
              >
                {t`Tap to sync changes`}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * OfflineBadge - Compact indicator for headers/toolbars
 */
export const OfflineBadge = ({ className }: { className?: string }) => {
  const { networkStatus } = useOffline();

  if (networkStatus === "online") return null;

  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        networkStatus === "offline" && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        networkStatus === "syncing" && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        className,
      )}
    >
      {networkStatus === "offline" && (
        <>
          <CloudSlashIcon className="size-3" weight="bold" />
          <span>{t`Offline`}</span>
        </>
      )}
      {networkStatus === "syncing" && (
        <>
          <ArrowsClockwiseIcon className="size-3 animate-spin" />
          <span>{t`Syncing`}</span>
        </>
      )}
    </div>
  );
};
